import {
  Auth,
  API
} from 'aws-amplify'
import axios from 'axios'

/**
 * ファイルをダウンロードする
 * @param {string} downloadUrl ダウンロード元
 * @param {file} file ダウンロードファイル
 * @returns {promise} コールバック
 */
export function downloadFile (downloadUrl) {
  return axios.get(downloadUrl, {
    responseType: 'blob'
  })
}

/**
 * ファイルをアップロードする
 * @param {string} apiUrl アップロード先
 * @param {file} file アップロードファイル
 * @returns {promise} コールバック
 */
export function uploadFile (apiUrl, file) {
  return axios.put(apiUrl, file, {
    headers: {
      'Content-Type': file.type
    }
  })
}

/**
 * ファイル交換一覧を取得する
 * @returns {promise} コールバック
 */
export function getExchanges (lastEvaluatedKey) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    let param = ''
    if (Object.keys(lastEvaluatedKey).length > 0) {
      param =
        'exchange_id=' +
        lastEvaluatedKey.exchange_id +
        '&' +
        'uploaded_date=' +
        encodeURIComponent(lastEvaluatedKey.uploaded_date)
    }
    return API.get('FILE_EXCHANGE_API', '/file/exchanges?' + param, {
      headers: {
        Authorization: token
      }
    })
  })
}

/**
 * ファイル交換を登録する
 * @param {object} addresses 宛先
 * @param {string} password パスワード
 * @param {string} title 件名
 * @param {object} uploadFiles アップロードファイル
 * @param {string} comments コメント
 * @returns {promise} コールバック
 */
export function addExchange (addresses, password, title, uploadFiles, comments) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.post('FILE_EXCHANGE_API', '/file/transfers', {
      headers: {
        Authorization: token
      },
      body: {
        addresses,
        password,
        title,
        upload_files: uploadFiles,
        comments
      }
    })
  })
}

/**
 * ファイルアップロードが完了したことを通知する
 * @param {string} exchangeId 交換ID
 * @param {boolean} uploaded 完了状態
 * @param {string} password パスワード
 * @returns {promise} コールバック
 */
export function updateExchange (exchangeId, uploaded, password) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.put(
      'FILE_EXCHANGE_API',
      '/file/transfers/' + exchangeId + '/_notify', {
        headers: {
          Authorization: token
        },
        body: {
          status: uploaded ? 'uploaded' : 'upload_failed',
          password
        }
      }
    )
  })
}

/**
 * ファイル交換を削除する
 * @param {string} exchangeId 交換ID
 * @returns {promise} コールバック
 */
export function deleteExchange (exchangeId) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.del('FILE_EXCHANGE_API', '/file/exchanges/' + exchangeId, {
      headers: {
        Authorization: token
      }
    })
  })
}

/**
 * アップロードファイル情報一覧を取得する
 * @param {string} exchangeId 交換ID
 * @returns {promise} コールバック
 */
export function getUploadedFiles (exchangeId) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.get('FILE_EXCHANGE_API', `/file/exchanges/${exchangeId}/files`, {
      headers: {
        Authorization: token
      }
    })
  })
}

/**
 * アップロード済みファイル詳細を取得する
 * @returns {promise}
 */
export function getUploadedFile (exchangeId, fileId) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.get(
      'FILE_EXCHANGE_API',
      `/file/exchanges/${exchangeId}/files/${fileId}`, {
        headers: {
          Authorization: token
        }
      }
    )
  })
}

/**
 * アップロードファイル情報を削除する
 *
 * @param {string} exchangeId 交換ID
 * @param {string} fileId ファイルID
 * @returns {promise} コールバック
 */
export function deleteUploadedFile (exchangeId, fileId) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.del(
      'FILE_EXCHANGE_API',
      `/file/exchanges/${exchangeId}/files/${fileId}`, {
        headers: {
          Authorization: token
        }
      }
    )
  })
}

/**
 * ダウンロードログインをする
 *
 * @param {string} exchangeId 交換ID
 * @param {string} email メールアドレス
 * @param {string} password パスワード
 * @returns {promise} コールバック
 */
export function getDownloadFiles (exchangeId, email, password) {
  return API.post(
    'FILE_EXCHANGE_API',
    `/download/exchanges/${exchangeId}/login`, {
      body: {
        email,
        password
      }
    }
  )
}

/**
 * アップロードファイルをダウンロードする
 * @param {string} exchangeId 交換ID
 * @param {string} fileId ファイルID
 * @returns {promise} コールバック
 */
export async function downloadUploadedFile (exchangeId, fileId) {
  const uploadedFile = await getUploadedFile(exchangeId, fileId)
  return downloadFile(uploadedFile.download_url)
}

/**
 * 交換ファイルをアップロードする
 * @param {*} addresses
 * @param {*} password
 * @param {*} title
 * @param {*} files
 * @param {*} comments
 */
export async function uploadExchangeFiles (
  addresses,
  password,
  title,
  files,
  comments
) {
  const uploadFiles = []
  for (const file of files) {
    uploadFiles.push({
      name: file.name,
      size: file.size,
      type: file.type
    })
  }
  const exchangeResponse = await addExchange(
    addresses,
    password,
    title,
    uploadFiles,
    comments
  )

  const uploadPromises = []
  for (let url of exchangeResponse.upload_urls) {
    const file = files.find(file => file.name === url.file_name)
    if (file) {
      uploadPromises.push(uploadFile(url.upload_url, file))
    }
  }

  try {
    await Promise.all(uploadPromises)
  } catch (error) {
    await updateExchange(exchangeResponse.exchange_id, false, password)
    throw error
  }

  return updateExchange(exchangeResponse.exchange_id, true, password)
}

/**
 * Login for receiver upload file
 * @param {string} exchangeId exchangeId
 * @param {string} email email of user
 * @param {string} password password of user
 */
export function transferUploadLogin (exchangeId, email, password) {
  return API.post('FILE_EXCHANGE_API', '/upload/' + exchangeId + '/login', {
    body: {
      email,
      password
    }
  })
}

/**
 * Upload the files that was uploaded by receiver
 * @param {exchangeId} exchangeId exchange id
 * @param {File} files files
 * @param {string} unsignedComments comments
 */
export async function uploadTransferFiles (exchangeId, files, unsignedComments) {
  const uploadFiles = []
  for (const file of files) {
    uploadFiles.push({
      name: file.name,
      size: file.size,
      type: file.type
    })
  }

  const uploadResponse = await uploadReceiverFile(
    exchangeId,
    uploadFiles,
    unsignedComments
  )

  const uploadPromises = []
  for (let url of uploadResponse.upload_urls) {
    const file = files.find(file => file.name === url.file_name)
    if (file) {
      uploadPromises.push(uploadFile(url.upload_url, file))
    }
  }

  try {
    await Promise.all(uploadPromises)
  } catch (error) {
    await updateTransfer(uploadResponse.exchange_id, false)
    throw error
  }

  return updateTransfer(uploadResponse.exchange_id, true)
}

/**
 * upload receiver files
 * @param {exchangeId} exchangeId exchange id
 * @param {array} uploadFiles upload file list
 * @param {string} unsignedComments comments
 */
export function uploadReceiverFile (exchangeId, uploadFiles, unsignedComments) {
  return API.post('FILE_EXCHANGE_API', '/file/receiver/upload', {
    body: {
      upload_files: uploadFiles,
      unsigned_comments: unsignedComments,
      exchange_id: exchangeId
    }
  })
}

/**
 * Update transfer status
 * @param {string} exchangeId exchangeId
 * @param {boolean} uploaded uploaded status
 */
export function updateTransfer (exchangeId, uploaded) {
  return API.put(
    'FILE_EXCHANGE_API',
    '/file/pickups/' + exchangeId + '/_notify', {
      body: {
        status: uploaded ? 'uploaded' : 'upload_failed'
      }
    }
  )
}

/**
 * Exchange file pick up
 * @param {string} title title
 * @param {string} addresses email address
 * @param {string} password password
 * @param {string} comments comments
 */
export function exchangeFilePickUp (title, addresses, password, comments) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.post('FILE_EXCHANGE_API', '/file/pickups', {
      body: {
        title,
        addresses,
        password,
        comments
      },
      headers: {
        Authorization: token
      }
    })
  })
}

/**
 * Exchange usage status
 * @param {string} startMonth start month
 * @param {string} endMonth  end month
 */
export function getExchangeUsage (startMonth, endMonth) {
  return Auth.currentAuthenticatedUser().then(user => {
    const token = user.signInUserSession.idToken.jwtToken
    return API.get(
      'FILE_EXCHANGE_API',
      `/file/exchanges/usage?start=${startMonth}&end=${endMonth}`, {
        headers: {
          Authorization: token
        }
      }
    )
  })
}
